import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fixturesAPI, seriesAPI, divisionsAPI, groundsAPI } from '../../services/api';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';

// ─── Nav Bar (shared across Generator / Team Health / Schedule) ───────────────
function PageNav({ active }) {
  const navigate = useNavigate();
  const items = [
    { key: 'generator',   icon: '⚡', label: 'Generator',   path: '/generator' },
    { key: 'health',      icon: '🩺', label: 'Team Health', path: '/team-health' },
    { key: 'schedule',    icon: '📋', label: 'Schedule',    path: '/schedule' },
  ];
  return (
    <div style={{ display:'flex', gap:6, marginBottom:20 }}>
      {items.map(item => (
        <button key={item.key}
          onClick={() => navigate(item.path)}
          style={{
            fontSize:12, fontWeight:600, padding:'6px 14px', borderRadius:6, cursor:'pointer',
            border:'1px solid', transition:'all .15s',
            background: active===item.key ? 'var(--accent)' : 'var(--surface)',
            color:       active===item.key ? 'white'        : 'var(--mid)',
            borderColor: active===item.key ? 'var(--accent)' : 'var(--border)',
          }}>
          {item.icon} {item.label}
        </button>
      ))}
    </div>
  );
}

// ─── Severity config ──────────────────────────────────────────────────────────
const SEV = {
  critical: { color:'#EF4444', bg:'rgba(239,68,68,.06)', border:'rgba(239,68,68,.25)', icon:'🔴', label:'CRITICAL' },
  high:     { color:'#F97316', bg:'rgba(249,115,22,.06)', border:'rgba(249,115,22,.25)', icon:'🟠', label:'HIGH' },
  medium:   { color:'#F59E0B', bg:'rgba(245,158,11,.06)', border:'rgba(245,158,11,.25)', icon:'🟡', label:'MEDIUM' },
  info:     { color:'#6B7280', bg:'rgba(107,114,128,.04)', border:'rgba(107,114,128,.2)', icon:'⚪', label:'INFO' },
};
const TYPE_LABEL = {
  doubleBooked:'Double Booked', veryLongGap:'2-Month+ Gap',
  longGap:'Long Gap', shortGap:'Back-to-Back', gameCount:'Low Game Count',
};

const MONTH_LABELS = { '05':'May', '06':'Jun', '07':'Jul', '08':'Aug' };

export default function TeamHealthPage() {
  const navigate = useNavigate();

  // ── Data ────────────────────────────────────────────────────────────────────
  const [seriesList,   setSeriesList]   = useState([]);
  const [selSeries,    setSelSeries]    = useState('');
  const [allFixtures,  setAllFixtures]  = useState([]);
  const [divisions,    setDivisions]    = useState([]);
  const [blackouts,    setBlackouts]    = useState([]); // [{id, start_date, end_date, reason}]
  const [noAvailDates, setNoAvailDates] = useState([]); // Sundays where no ground is available
  const [wizardNoGame, setWizardNoGame] = useState({ div: [], team: [] }); // from generator localStorage
  const [loading,      setLoading]      = useState(false);

  // ── Controls ────────────────────────────────────────────────────────────────
  const [healthChecks, setHealthChecks] = useState({
    doubleBooked: true, veryLongGap: true, longGap: true,
    gameCount: true, shortGap: false,
  });
  const [longGapThreshold,  setLongGapThreshold]  = useState(28);
  const [shortGapThreshold, setShortGapThreshold] = useState(7);
  const [divFilter,         setDivFilter]         = useState('');
  const [monthFilter,       setMonthFilter]        = useState(''); // 'YYYY-MM'
  const [selectedTeam,      setSelectedTeam]       = useState(null); // highlighted in calendar

  // ── Calendar ref for scrolling to highlighted row ───────────────────────────
  const calendarRef = useRef(null);
  const highlightedRowRef = useRef(null);

  // ── Load series list ─────────────────────────────────────────────────────────
  useEffect(() => {
    seriesAPI.list().then(r => {
      const list = r.data || [];
      setSeriesList(list);
      if (list.length > 0) setSelSeries(list[0].id);
    }).catch(() => toast.error('Failed to load series'));
  }, []);

  // ── Load fixtures + divisions when series changes ────────────────────────────
  useEffect(() => {
    if (!selSeries) return;
    setLoading(true);
    // Load wizard no-game-days from localStorage (set by Generator page)
    try {
      const raw = localStorage.getItem('michca_generator_config');
      if (raw) {
        const cfg = JSON.parse(raw);
        setWizardNoGame({
          div: cfg.noGameDays || [],
          team: cfg.noGameDaysTeam || [],
        });
      }
    } catch { /* ignore */ }

    Promise.all([
      fixturesAPI.list({ series_id: selSeries }).catch(() => ({ data: [] })),
      divisionsAPI.list(selSeries).catch(() => ({ data: [] })),
      groundsAPI.getBlackouts(selSeries).catch(() => ({ data: [] })),
      groundsAPI.list().catch(() => ({ data: [] })),
    ]).then(async ([fixRes, divRes, blkRes, groundsRes]) => {
      setAllFixtures(fixRes.data || []);
      setDivisions(divRes.data || []);
      setBlackouts(blkRes.data || []);
      // Determine Sundays where NO ground has availability
      const grounds = groundsRes.data || [];
      const availBySunday = {}; // date → Set of ground_ids available
      await Promise.all(grounds.map(async g => {
        try {
          const av = await groundsAPI.getAvailability(g.id, { series_id: selSeries });
          const records = av.data?.availability || av.data || [];
          records.filter(r => r.status === 'available').forEach(r => {
            const d = String(r.available_date || '').split('T')[0];
            if (!availBySunday[d]) availBySunday[d] = new Set();
            availBySunday[d].add(g.id);
          });
        } catch { /* ignore */ }
      }));
      // Find Sundays from fixtures that have zero grounds available
      const allSundays = [...new Set((fixRes.data || []).map(f => String(f.match_date || '').split('T')[0]).filter(Boolean))];
      setNoAvailDates(allSundays.filter(d => !availBySunday[d] || availBySunday[d].size === 0));
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, [selSeries]);

  // ── Scroll highlighted row into view ────────────────────────────────────────
  useEffect(() => {
    if (selectedTeam && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({ behavior:'smooth', block:'nearest' });
    }
  }, [selectedTeam]);

  // ── Health data computation ──────────────────────────────────────────────────
  const { teamMap, issues, summary, allDates } = useMemo(() => {
    const confirmed = allFixtures.filter(f => f.status === 'confirmed');
    if (!confirmed.length) return { teamMap:{}, issues:[], summary:{}, allDates:[] };

    const allDates = [...new Set(
      confirmed.map(f => String(f.match_date||'').split('T')[0]).filter(Boolean)
    )].sort();

    const teamMap = {};
    confirmed.forEach(f => {
      const d = String(f.match_date||'').split('T')[0];
      if (!d) return;
      const add = (tid, short, div, divId, role) => {
        if (!tid) return;
        if (!teamMap[tid]) teamMap[tid] = { short, div, divId, id:tid, dates:new Set(), games:[], gameByDate:{} };
        teamMap[tid].dates.add(d);
        const opponentId = role === 'home' ? f.away_team_id : f.home_team_id;
        const opponentShort = role === 'home' ? f.away_short : f.home_short;
        const g = { date:d, opponent: opponentShort, opponent_id: opponentId, ground:f.ground_name, role };
        teamMap[tid].games.push(g);
        teamMap[tid].gameByDate[d] = g;
      };
      add(f.home_team_id, f.home_short, f.division_name, f.division_id, 'home');
      add(f.away_team_id, f.away_short, f.division_name, f.division_id, 'away');
    });
    Object.values(teamMap).forEach(t => t.games.sort((a,b) => a.date.localeCompare(b.date)));

    const issues = [];
    Object.values(teamMap).forEach(team => {
      const { div, games } = team;
      if (!games.length) return;

      if (healthChecks.doubleBooked) {
        const byDate = {};
        games.forEach(g => { byDate[g.date] = (byDate[g.date]||[]); byDate[g.date].push(g); });
        Object.entries(byDate).forEach(([date, gs]) => {
          if (gs.length > 1) issues.push({ type:'doubleBooked', severity:'critical', team:team.short, div,
            detail:`${gs.length} games on same day (${date.substring(5)})`,
            games: gs.map(g=>`${g.role==='home'?'vs':'@'} ${g.opponent}`),
          });
        });
      }
      for (let i = 1; i < games.length; i++) {
        const gapDays = Math.round((new Date(games[i].date+'T12:00:00Z') - new Date(games[i-1].date+'T12:00:00Z')) / 86400000);
        const gw = Math.round(gapDays/7);
        if (healthChecks.veryLongGap && gapDays >= 56)
          issues.push({ type:'veryLongGap', severity:'high', team:team.short, div,
            from:games[i-1].date, to:games[i].date, gapDays, gapWeeks:gw,
            detail:`${gw} weeks between games (${games[i-1].date.substring(5)} → ${games[i].date.substring(5)})`,
            games:[`Last: vs ${games[i-1].opponent}`, `Next: vs ${games[i].opponent}`],
          });
        else if (healthChecks.longGap && gapDays >= longGapThreshold && gapDays < 56)
          issues.push({ type:'longGap', severity:'medium', team:team.short, div,
            from:games[i-1].date, to:games[i].date, gapDays, gapWeeks:gw,
            detail:`${gw} weeks gap (${games[i-1].date.substring(5)} → ${games[i].date.substring(5)})`,
            games:[`Last: vs ${games[i-1].opponent}`, `Next: vs ${games[i].opponent}`],
          });
        if (healthChecks.shortGap && gapDays <= shortGapThreshold)
          issues.push({ type:'shortGap', severity:'info', team:team.short, div,
            detail:`Only ${gapDays} days between games`, gapDays,
            games:[`Game 1: vs ${games[i-1].opponent}`, `Game 2: vs ${games[i].opponent}`],
          });
      }
      if (healthChecks.gameCount) {
        const divTeams = Object.values(teamMap).filter(t => t.div === div);
        const counts = divTeams.map(t => t.games.length).sort((a,b)=>a-b);
        const median = counts[Math.floor(counts.length/2)];
        if (games.length < median - 1)
          issues.push({ type:'gameCount', severity:'medium', team:team.short, div,
            detail:`Only ${games.length} games (division median: ${median})`,
            games: games.map(g=>`${g.date.substring(5)}: vs ${g.opponent}`),
          });
      }
    });

    const allGaps = [];
    Object.values(teamMap).forEach(t => {
      for (let i=1; i<t.games.length; i++)
        allGaps.push(Math.round((new Date(t.games[i].date+'T12:00:00Z')-new Date(t.games[i-1].date+'T12:00:00Z'))/86400000));
    });
    allGaps.sort((a,b)=>a-b);
    const summary = {
      totalTeams: Object.keys(teamMap).length,
      minGapDays: allGaps[0]||0,
      maxGapDays: allGaps[allGaps.length-1]||0,
      medianGapDays: allGaps[Math.floor(allGaps.length/2)]||0,
      avgGapDays: allGaps.length ? Math.round(allGaps.reduce((s,v)=>s+v,0)/allGaps.length) : 0,
    };
    return { teamMap, issues, summary, allDates };
  }, [allFixtures, healthChecks, longGapThreshold, shortGapThreshold]);

  // ── Filtered issues ──────────────────────────────────────────────────────────
  const filteredIssues = useMemo(() => {
    let list = issues;
    if (divFilter) list = list.filter(i => i.div === divFilter);
    return list.sort((a,b) => ({ critical:0,high:1,medium:2,info:3 }[a.severity]||4) - ({ critical:0,high:1,medium:2,info:3 }[b.severity]||4));
  }, [issues, divFilter]);

  // ── Calendar filtered dates (by month) ──────────────────────────────────────
  const calDates = useMemo(() => {
    if (!monthFilter) return allDates;
    return allDates.filter(d => d.startsWith(monthFilter));
  }, [allDates, monthFilter]);

  // ── Calendar filtered teams (by division) ───────────────────────────────────
  const calTeams = useMemo(() => {
    return Object.values(teamMap)
      .filter(t => !divFilter || t.div === divFilter)
      .sort((a,b) => a.div.localeCompare(b.div) || a.short.localeCompare(b.short));
  }, [teamMap, divFilter]);

  // ── Div label shortener ───────────────────────────────────────────────────────
  const divShort = div => {
    if (!div) return '?';
    if (div.toLowerCase().includes('premier')) return 'P';
    const m = div.match(/\d+/);
    return m ? `D${m[0]}` : div.substring(0,2);
  };
  const divColorMap = { Premier:'#F5A623','Division 1':'#4A90D9','Division 2':'#7B68EE','Division 3':'#00C896' };
  const divColor = div => divColorMap[div] || 'var(--accent)';

  // ── Available months from data ───────────────────────────────────────────────
  const availMonths = useMemo(() => {
    return [...new Set(allDates.map(d => d.substring(0,7)))].sort();
  }, [allDates]);

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'60vh' }}>
      <Spinner size={32} />
    </div>
  );

  return (
    <div style={{ maxWidth:1400, margin:'0 auto', padding:'0 20px 60px' }}>

      {/* Header */}
      <div style={{ padding:'20px 0 4px' }}>
        <PageNav active="health" />
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:22, fontWeight:900, color:'var(--text)' }}>🩺 Team Health</div>
            <div style={{ fontSize:12, color:'var(--mid)', marginTop:2 }}>
              Analyse game gaps, double-bookings and low game counts across all teams
            </div>
          </div>
          {/* Series selector */}
          <div>
            <select className="input select" style={{ fontSize:13, fontWeight:600, width:180 }}
              value={selSeries} onChange={e => setSelSeries(e.target.value)}>
              {seriesList.map(s => <option key={s.id} value={s.id}>{s.name || s.short_name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display:'grid', gridTemplateColumns:'55% 1fr', gap:16, alignItems:'start' }}>

        {/* ── LEFT: Controls + Stats + Issue Cards ── */}
        <div>

          {/* Check toggles */}
          <div className="card" style={{ padding:'12px 14px', marginBottom:10 }}>
            <div style={{ fontSize:9, fontWeight:800, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>
              Select Checks to Run
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
              {[
                { key:'doubleBooked', label:'🔴 Double-booked', desc:'2 games same day' },
                { key:'veryLongGap',  label:'🟠 2-month+ gap',  desc:'≥8 weeks between games' },
                { key:'longGap',      label:'🟡 Long gap',      desc:'≥ threshold between games' },
                { key:'gameCount',    label:'🔵 Low game count', desc:'Below division median' },
                { key:'shortGap',     label:'⚪ Back-to-back',  desc:'≤ threshold apart' },
              ].map(c => (
                <button key={c.key} title={c.desc}
                  onClick={() => setHealthChecks(p => ({ ...p, [c.key]:!p[c.key] }))}
                  style={{ fontSize:11, padding:'5px 10px', borderRadius:20, cursor:'pointer',
                    border:'1px solid', transition:'all .15s',
                    background: healthChecks[c.key] ? 'var(--accent)' : 'var(--surface)',
                    color:       healthChecks[c.key] ? 'white'        : 'var(--dim)',
                    borderColor: healthChecks[c.key] ? 'var(--accent)' : 'var(--border)',
                    fontWeight:  healthChecks[c.key] ? 700 : 400 }}>
                  {c.label}
                </button>
              ))}
            </div>

            {/* Thresholds + Division filter */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
              <div>
                <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:3 }}>Long Gap (days)</div>
                <input type="number" className="input" min={7} max={120} value={longGapThreshold}
                  style={{ width:72, fontSize:12 }}
                  onChange={e => setLongGapThreshold(parseInt(e.target.value)||28)} />
              </div>
              <div>
                <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:3 }}>Short Gap (days)</div>
                <input type="number" className="input" min={1} max={14} value={shortGapThreshold}
                  style={{ width:72, fontSize:12 }}
                  onChange={e => setShortGapThreshold(parseInt(e.target.value)||7)} />
              </div>
              <div>
                <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:3 }}>Division</div>
                <select className="input select" style={{ fontSize:12, width:140 }}
                  value={divFilter} onChange={e => { setDivFilter(e.target.value); setSelectedTeam(null); }}>
                  <option value="">All Divisions</option>
                  {divisions.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* 6-stat summary bar */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:6, marginBottom:10 }}>
            {[
              { label:'Issues Found',   value: filteredIssues.length,   color: filteredIssues.length > 0 ? 'var(--red)' : 'var(--accent)' },
              { label:'Teams Analysed', value: summary.totalTeams||0,   color:'var(--mid)' },
              { label:'Shortest Gap',   value: summary.minGapDays ? `${summary.minGapDays}d` : '—',    color:'#22C55E' },
              { label:'Longest Gap',    value: summary.maxGapDays ? `${summary.maxGapDays}d` : '—',    color: (summary.maxGapDays||0)>=56?'var(--red)':(summary.maxGapDays||0)>=28?'#F59E0B':'var(--accent)' },
              { label:'Avg Gap',        value: summary.avgGapDays    ? `${summary.avgGapDays}d` : '—',  color:'var(--mid)' },
              { label:'Median Gap',     value: summary.medianGapDays ? `${summary.medianGapDays}d` : '—', color:'var(--mid)' },
            ].map(s => (
              <div key={s.label} style={{ textAlign:'center', padding:'8px 4px',
                  background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)' }}>
                <div style={{ fontSize:8, color:'var(--dim)', fontWeight:700, textTransform:'uppercase', marginBottom:2 }}>{s.label}</div>
                <div style={{ fontSize:16, fontWeight:900, color:s.color, fontFamily:"'Roboto Mono',monospace" }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Issue cards */}
          {filteredIssues.length === 0 ? (
            <div className="card" style={{ padding:'40px 20px', textAlign:'center', color:'var(--dim)', fontSize:13 }}>
              {allFixtures.filter(f=>f.status==='confirmed').length === 0
                ? '🗓 No schedule loaded — generate a schedule first'
                : '✅ No issues found with the selected checks and filters'}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              {filteredIssues.map((issue, idx) => {
                const sev = SEV[issue.severity] || SEV.info;
                const isSelected = selectedTeam === issue.team;
                return (
                  <div key={idx}
                    onClick={() => setSelectedTeam(isSelected ? null : issue.team)}
                    style={{ display:'flex', gap:10, padding:'10px 12px', borderRadius:8, cursor:'pointer',
                      background: isSelected ? `color-mix(in srgb, ${sev.color} 12%, var(--surface))` : sev.bg,
                      border:`1px solid ${isSelected ? sev.color : sev.border}`,
                      transition:'all .15s',
                    }}>
                    {/* Severity */}
                    <div style={{ minWidth:100, flexShrink:0 }}>
                      <div style={{ fontSize:9, fontWeight:800, color:sev.color, textTransform:'uppercase', letterSpacing:'.4px' }}>
                        {sev.icon} {sev.label}
                      </div>
                      <div style={{ fontSize:10, color:'var(--dim)', marginTop:2 }}>{TYPE_LABEL[issue.type]||issue.type}</div>
                    </div>
                    {/* Team + detail */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                        <span style={{ fontSize:13, fontWeight:800, color:'var(--text)' }}>{issue.team}</span>
                        <span style={{ fontSize:9, padding:'1px 6px', borderRadius:8,
                          background:`color-mix(in srgb, ${divColor(issue.div)} 15%, transparent)`,
                          color:divColor(issue.div), fontWeight:700 }}>
                          {issue.div?.replace('Division ','D')||'?'}
                        </span>
                        {isSelected && <span style={{ fontSize:9, color:'var(--accent)', fontWeight:700 }}>← highlighted in calendar</span>}
                      </div>
                      <div style={{ fontSize:11, color:'var(--mid)', lineHeight:1.5 }}>{issue.detail}</div>
                    </div>
                    {/* Adjacent games */}
                    <div style={{ minWidth:160, maxWidth:200, flexShrink:0 }}>
                      {(issue.games||[]).slice(0,4).map((g,i) => (
                        <div key={i} style={{ fontSize:10, color:'var(--dim)', padding:'1px 0',
                          borderBottom: i<Math.min(issue.games.length,4)-1?'1px solid var(--border)':'none',
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {g}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── RIGHT: Team Game Calendar (sticky) ── */}
        <div style={{ position:'sticky', top:20 }}>
          <div className="card" style={{ padding:'12px 14px', maxHeight:'calc(100vh - 100px)', overflow:'hidden', display:'flex', flexDirection:'column' }}>

            {/* Calendar header + filters */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10, flexShrink:0 }}>
              <div style={{ fontSize:11, fontWeight:800, color:'var(--text)', textTransform:'uppercase', letterSpacing:'.4px' }}>
                Team Game Calendar
                <span style={{ fontSize:9, color:'var(--dim)', fontWeight:400, textTransform:'none', marginLeft:6 }}>
                  {calTeams.length} teams × {calDates.length} Sundays
                </span>
              </div>
            </div>

            {/* Month filter pills */}
            <div style={{ display:'flex', gap:5, marginBottom:8, flexShrink:0, flexWrap:'wrap' }}>
              <button onClick={() => setMonthFilter('')}
                style={{ fontSize:10, padding:'3px 8px', borderRadius:10, cursor:'pointer', border:'1px solid',
                  background: !monthFilter ? 'var(--accent)' : 'var(--surface)',
                  color: !monthFilter ? 'white' : 'var(--mid)',
                  borderColor: !monthFilter ? 'var(--accent)' : 'var(--border)' }}>
                All
              </button>
              {availMonths.map(m => {
                const mo = m.substring(5,7);
                return (
                  <button key={m} onClick={() => setMonthFilter(monthFilter===m?'':m)}
                    style={{ fontSize:10, padding:'3px 8px', borderRadius:10, cursor:'pointer', border:'1px solid',
                      background: monthFilter===m ? 'var(--accent)' : 'var(--surface)',
                      color: monthFilter===m ? 'white' : 'var(--mid)',
                      borderColor: monthFilter===m ? 'var(--accent)' : 'var(--border)' }}>
                    {MONTH_LABELS[mo]||m}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display:'flex', gap:12, marginBottom:8, flexShrink:0, flexWrap:'wrap' }}>
              {[
                { color:'var(--accent)', label:'game played' },
                { color:'rgba(239,68,68,.3)', label:'long gap' },
                { color:'rgba(255,215,0,.4)', label:'selected team' },
              ].map(l => (
                <span key={l.label} style={{ display:'flex', alignItems:'center', gap:4, fontSize:9, color:'var(--dim)' }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:l.color, border:'1px solid var(--border)', flexShrink:0 }} />
                  {l.label}
                </span>
              ))}
              <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:9, color:'var(--dim)' }}>
                <div style={{ width:10, height:10, borderRadius:2, background:'rgba(239,68,68,.2)', border:'1px solid rgba(239,68,68,.3)',
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:900, color:'#EF4444', lineHeight:1 }}>✕</div>
                no game day
              </span>
            </div>

            {/* Calendar grid — scrollable */}
            {calTeams.length === 0 ? (
              <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--dim)', fontSize:12 }}>
                No teams to display
              </div>
            ) : (
              <div ref={calendarRef} style={{ overflowX:'auto', overflowY:'auto', flex:1 }}>
                <table style={{ borderCollapse:'collapse', fontSize:9, width:'max-content' }}>
                  <thead>
                    <tr>
                      {/* Division col */}
                      <th style={{ width:18, minWidth:18, position:'sticky', left:0, background:'var(--card)', zIndex:2,
                        borderBottom:'2px solid var(--border)', borderRight:'1px solid var(--border)', padding:0 }} />
                      {/* Team col */}
                      <th style={{ width:44, minWidth:44, position:'sticky', left:18, background:'var(--card)', zIndex:2,
                        borderBottom:'2px solid var(--border)', borderRight:'2px solid var(--border)',
                        fontSize:8, color:'var(--dim)', fontWeight:600, padding:'2px 4px', textAlign:'left' }}>
                        Team
                      </th>
                      {/* Date cols */}
                      {calDates.map(d => {
                        const [,mo,day] = d.split('-');
                        return (
                          <th key={d} style={{ width:20, minWidth:20, borderBottom:'2px solid var(--border)',
                            fontSize:7, color:'var(--dim)', fontWeight:600, textAlign:'center',
                            writingMode:'vertical-rl', padding:'2px 1px', height:52, verticalAlign:'bottom' }}>
                            {mo}/{day}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {calTeams.map(team => {
                      const isHighlighted = selectedTeam === team.short;
                      // Compute long gap dates for red tinting
                      const longGapDates = new Set();
                      for (let i=1; i<team.games.length; i++) {
                        const gd = Math.round((new Date(team.games[i].date+'T12:00:00Z')-new Date(team.games[i-1].date+'T12:00:00Z'))/86400000);
                        if (gd >= longGapThreshold) {
                          calDates.filter(d => d > team.games[i-1].date && d < team.games[i].date)
                            .forEach(d => longGapDates.add(d));
                        }
                      }
                      return (
                        <tr key={team.id || team.short}
                          ref={isHighlighted ? highlightedRowRef : null}
                          style={{ background: isHighlighted ? 'rgba(255,215,0,.08)' : 'transparent' }}
                          onClick={() => setSelectedTeam(isHighlighted ? null : team.short)}>
                          {/* Div badge */}
                          <td style={{ position:'sticky', left:0, background: isHighlighted ? 'rgba(255,215,0,.15)' : 'var(--card)',
                              zIndex:1, borderBottom:'1px solid var(--border)', borderRight:'1px solid var(--border)',
                              padding:'1px 2px', textAlign:'center', cursor:'pointer' }}>
                            <span style={{ fontSize:7, fontWeight:800, color:divColor(team.div),
                              background:`color-mix(in srgb, ${divColor(team.div)} 15%, transparent)`,
                              borderRadius:3, padding:'1px 2px' }}>
                              {divShort(team.div)}
                            </span>
                          </td>
                          {/* Team name */}
                          <td style={{ position:'sticky', left:18, background: isHighlighted ? 'rgba(255,215,0,.15)' : 'var(--card)',
                              zIndex:1, borderBottom:'1px solid var(--border)', borderRight:'2px solid var(--border)',
                              padding:'2px 4px', fontWeight: isHighlighted ? 800 : 600,
                              color: isHighlighted ? 'var(--text)' : 'var(--accent)',
                              fontFamily:"'Roboto Mono',monospace", whiteSpace:'nowrap', cursor:'pointer',
                              fontSize:9 }}>
                            {team.short}
                          </td>
                          {/* Game cells */}
                          {calDates.map(d => {
                            const hasGame  = team.dates.has(d);
                            const isGap    = longGapDates.has(d);
                            // Check blackouts for this date
                            const blackout = blackouts.find(b => {
                              const start = String(b.start_date || '').split('T')[0];
                              const end = String(b.end_date || b.start_date || '').split('T')[0];
                              return d >= start && d <= end;
                            });
                            const noAvail = noAvailDates.includes(d);
                            // Wizard-level no-game-days (per-division from Generator localStorage)
                            const divBlackout = wizardNoGame.div.find(n => n.date === d && n.divisionName === team.div);
                            // Wizard-level no-game-days (per-team)
                            const teamBlackout = wizardNoGame.team.find(n => n.date === d && n.teamShort === team.short);
                            const noGameDay = !!blackout || noAvail || !!divBlackout || !!teamBlackout;
                            const reasons = [];
                            if (blackout) reasons.push(`Global: ${blackout.reason || 'blackout'}`);
                            if (noAvail) reasons.push('No ground available');
                            if (divBlackout) reasons.push(`${team.div} no-game-day`);
                            if (teamBlackout) reasons.push(`${team.short} no-game-day`);
                            const title = hasGame
                              ? `${team.short} plays ${d}`
                              : noGameDay ? `${d} — ${reasons.join(' · ')}`
                              : isGap ? `Long gap for ${team.short}` : '';
                            return (
                              <td key={d}
                                title={title}
                                style={{
                                  width:20, height:18, padding:1,
                                  borderBottom:'1px solid var(--border)',
                                  borderRight:'1px solid var(--border)',
                                  position:'relative',
                                }}>
                                <div style={{
                                  width:'100%', height:'100%', borderRadius:2,
                                  background: hasGame ? 'var(--accent)'
                                    : noGameDay ? 'rgba(239,68,68,.2)'
                                    : isGap ? 'rgba(239,68,68,.25)'
                                    : 'transparent',
                                  display:'flex', alignItems:'center', justifyContent:'center',
                                  fontSize:11, fontWeight:900, color:'#EF4444',
                                  lineHeight:1,
                                }}>
                                  {!hasGame && noGameDay ? '✕' : ''}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
